import { useCallback, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import { wsChannel } from '@xdocs/schemas';
import type { Comment, CommentListResult, ErrorAlert } from '@xdocs/schemas';
import { useWs } from '../../ws/WsProvider.js';

const PAGE_SIZE = 20;

export interface UseCommentsResult {
  rows: readonly CommentListResult['rows'][number][];
  total: number | null;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  post: (body: string, parentCommentId?: string) => Promise<Comment>;
  isPosting: boolean;
  remove: (commentId: string) => Promise<{ commentId: string; removed: boolean }>;
  isRemoving: boolean;
}

/**
 * `comment.list`/`comment.create`/`comment.remove` (F28) for ONE thread page
 * — either the top-level thread (`parentCommentId` omitted) or one reply
 * page (`parentCommentId` set, lazily fetched only once `options.enabled`
 * flips true — a collapsed "View N replies" row never fires the request).
 * Kept live via `share:{shareId}`'s `comment_added`/`comment_removed`
 * events — "signal, not payload" (`sharePush`'s own doc comment): this just
 * invalidates and refetches.
 */
export function useComments(shareId: string, parentCommentId?: string, options?: { enabled?: boolean }): UseCommentsResult {
  const { wsClient, connectionState } = useWs();
  const queryClient = useQueryClient();
  const enabled = connectionState === 'connected' && (options?.enabled ?? true);
  const queryKey = ['share', shareId, 'comments', parentCommentId ?? null] as const;

  const query = useQuery<CommentListResult, ErrorAlert>({
    queryKey,
    queryFn: () =>
      requestTyped(wsClient, 'comment.list', {
        shareId,
        page: 1,
        pageSize: PAGE_SIZE,
        sort: 'createdAt',
        dir: 'asc',
        ...(parentCommentId ? { parentCommentId } : {}),
      }),
    enabled,
  });

  useEffect(() => {
    const channel = wsChannel.share(shareId);
    return wsClient.subscribe(channel, () => {
      void queryClient.invalidateQueries({ queryKey: ['share', shareId, 'comments'] });
    });
  }, [wsClient, queryClient, shareId]);

  const postMutation = useMutation<Comment, ErrorAlert, { body: string; parentCommentId?: string }>({
    mutationFn: (input) => requestTyped(wsClient, 'comment.create', { shareId, body: input.body, ...(input.parentCommentId ? { parentCommentId: input.parentCommentId } : {}) }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['share', shareId, 'comments'] }),
  });

  const removeMutation = useMutation<{ commentId: string; removed: boolean }, ErrorAlert, string>({
    mutationFn: (commentId) => requestTyped(wsClient, 'comment.remove', { commentId }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['share', shareId, 'comments'] }),
  });

  const post = useCallback((body: string, replyTo?: string) => postMutation.mutateAsync({ body, ...(replyTo ? { parentCommentId: replyTo } : {}) }), [postMutation]);
  const remove = useCallback((commentId: string) => removeMutation.mutateAsync(commentId), [removeMutation]);

  return {
    rows: query.data?.rows ?? [],
    total: query.data?.total ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    onRetry: () => void query.refetch(),
    post,
    isPosting: postMutation.isPending,
    remove,
    isRemoving: removeMutation.isPending,
  };
}
